import { ComponentProps, forwardRef, RefObject, ReactNode, useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@restworld/utils-common';

import { IconButtonVariantType, IconComponentType } from '../icon-button';
import { Spinner } from '../spinner';
import { StyledButtonNotificationIndicator } from './button.helpers';
import { focusRing, focusRingReversed } from '../styles/focus-ring';

/**
 * Button variant styles using CVA.
 *
 * Variants:
 * - primary: Purple text on fluo background (CTA)
 * - secondary-*: Colored text on tinted background
 * - tertiary / tertiary-reversed: Outlined / transparent
 *
 * Sizes:
 * - sm: 32px height, 14px text
 * - md: 48px height, 16px text (default)
 */
const buttonVariants = cva(
  // Shared WCAG focus ring (purple-500); reversed variants below override it to white.
  `relative flex gap-2 items-center justify-center font-semibold transition duration-200 ease-in-out rounded-lg !py-2 ${focusRing} disabled:bg-neutral-40 disabled:text-neutral-200 disabled:cursor-not-allowed`,
  {
    variants: {
      variant: {
        primary: '!text-purple-700 bg-fluo-500',
        'secondary-neutral': 'text-neutral-700 bg-neutral-900/10',
        'secondary-restworld': 'text-purple-700 bg-purple-500/10',
        'secondary-success': 'text-green-700 bg-green-700/10',
        'secondary-warning': 'text-orange-700 bg-orange-700/10',
        'secondary-danger': 'text-red-700 bg-red-700/10',
        'secondary-reversed': `!text-neutral-10 bg-neutral-10/10 ${focusRingReversed}`,
        tertiary: "!text-neutral-700 bg-transparent shadow-[inset_0_0_0_1px_theme('colors.neutral.90')]",
        'tertiary-reversed': `!text-neutral-10 bg-transparent shadow-[inset_0_0_0_1px_theme('colors.neutral.90')] ${focusRingReversed}`,
        // Borderless (replaces master's old-ds `ghost`/`borderless`): transparent, no border,
        // hover handled by the overlay. For icon-only back/close buttons and inline text toggles.
        ghost: '!text-neutral-700 bg-transparent',
      },
      size: {
        sm: '!text-sm !px-3 !h-8',
        md: '!text-base !h-12 !px-4',
      },
      fullwidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

/** Maps button variant to icon text color */
const iconColorMap: Record<ButtonVariantType, string> = {
  primary: 'text-purple-700',
  'secondary-neutral': 'text-neutral-700',
  'secondary-success': 'text-green-700',
  'secondary-warning': 'text-orange-700',
  'secondary-danger': 'text-red-700',
  'secondary-restworld': 'text-purple-700',
  'secondary-reversed': 'text-neutral-10',
  tertiary: 'text-neutral-700',
  'tertiary-reversed': 'text-neutral-10',
  ghost: 'text-neutral-700',
};

/** Maps button variant to Spinner color */
const spinnerColorMap: Record<ButtonVariantType, IconButtonVariantType> = {
  primary: 'restworld',
  'secondary-neutral': 'neutral',
  'secondary-success': 'success',
  'secondary-warning': 'warning',
  'secondary-danger': 'error',
  'secondary-restworld': 'restworld',
  'secondary-reversed': 'reversed',
  tertiary: 'neutral',
  'tertiary-reversed': 'reversed',
  ghost: 'neutral',
};

/** Maps button variant to hover overlay background */
const overlayBgMap: Record<ButtonVariantType, string> = {
  primary: 'bg-neutral-700',
  'secondary-neutral': 'bg-neutral-900',
  'secondary-success': 'bg-green-700',
  'secondary-warning': 'bg-orange-700',
  'secondary-danger': 'bg-red-700',
  'secondary-restworld': 'bg-purple-500',
  'secondary-reversed': 'bg-neutral-10',
  tertiary: 'bg-neutral-700',
  'tertiary-reversed': 'bg-white',
  ghost: 'bg-neutral-700',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    StartIcon,
    EndIcon,
    children,
    disabled,
    fullwidth,
    className,
    indicator,
    loading,
    onClick,
    size = 'md',
    variant = 'primary',
    testId,
    ...restProps
  } = props;

  const iconSizeClasses = '!h-5 !w-5 min-h-[20px] min-w-[20px]';
  const iconColorClasses = useMemo(() => iconColorMap[variant], [variant]);
  const iconClasses = useMemo(
    () => `${iconSizeClasses} ${disabled ? 'text-neutral-100' : iconColorClasses}`,
    [disabled, iconColorClasses]
  );
  // Icons render normally; the loading state is a single centered spinner in the render below
  // (mirrors the old ui-ds Button — no per-icon-slot spinners with the label still showing).
  const startIconEl = useMemo(
    () => (StartIcon ? <StartIcon className={iconClasses} /> : null),
    [StartIcon, iconClasses]
  );
  const endIconEl = useMemo(() => (EndIcon ? <EndIcon className={iconClasses} /> : null), [EndIcon, iconClasses]);

  const overlayClassName = cn(
    'absolute top-0 left-0 right-0 bottom-0 opacity-0 hover:opacity-15 active:opacity-20 transition duration-200 ease-in-out rounded-lg',
    overlayBgMap[variant],
    disabled ? 'hover:opacity-0 active:opacity-0' : ''
  );

  return (
    <button
      ref={ref}
      className={cn(
        buttonVariants({ variant, size, fullwidth: fullwidth || undefined }),
        loading || disabled ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer pointer-events-auto',
        // A disabled button must fully grey out, but variants like primary use `!text-purple-700`
        // (+ bg-fluo-500) which the `disabled:*` utilities can't override. Force it with plain
        // important classes gated on the prop (loading buttons keep their colour — disabled=false).
        disabled && '!bg-neutral-40 !text-neutral-200',
        className
      )}
      onClick={loading || disabled ? undefined : onClick}
      // Only a truly-disabled button gets the native `disabled` attr (and its gray `disabled:*`
      // styling). A loading button keeps its variant colour and is made inert via
      // pointer-events-none above — matching the old ui-ds Button.
      disabled={disabled}
      aria-busy={loading || undefined}
      data-testid={testId}
      {...restProps}
    >
      {/* Hide (don't remove) the content while loading so the button keeps its width, and overlay
          a single centered spinner. */}
      <span className={cn('flex items-center justify-center gap-2', loading && 'opacity-0')}>
        {startIconEl}
        {!!children && <span>{children}</span>}
        {endIconEl}
      </span>
      {loading && (
        <span className="absolute top-0 left-0 right-0 bottom-0 grid place-items-center">
          <Spinner size={size} color={spinnerColorMap[variant]} />
        </span>
      )}
      <span className={overlayClassName} />
      {!!indicator && (
        <StyledButtonNotificationIndicator>
          {typeof indicator === 'number' && <span className="!text-sm text-neutral-10 font-semibold">{indicator}</span>}
        </StyledButtonNotificationIndicator>
      )}
    </button>
  );
});
Button.displayName = 'Button';

export default Button;

export interface ButtonProps extends ComponentProps<'button'> {
  children?: ReactNode;
  ref?: ((instance: HTMLButtonElement | null) => void) | RefObject<HTMLButtonElement | null> | null;
  disabled?: boolean;
  StartIcon?: IconComponentType;
  EndIcon?: IconComponentType;
  className?: string;
  size?: ButtonSizeType;
  variant?: ButtonVariantType;
  fullwidth?: boolean;
  indicator?: boolean | number;
  loading?: boolean;
  testId?: string;
}

type ButtonSizeType = 'sm' | 'md';

export type ButtonVariantType =
  | 'primary'
  | 'secondary-neutral'
  | 'secondary-success'
  | 'secondary-warning'
  | 'secondary-danger'
  | 'secondary-restworld'
  | 'secondary-reversed'
  | 'tertiary'
  | 'tertiary-reversed'
  | 'ghost';

export { buttonVariants };
